import {
    Text,
    VStack,
    Flex,
    Button,
    useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";

import GranatechInternalDataCard from "../GranatechInternalDataCard";
import BMPDataCard from "../BMPDataCard";
import { PropostaFinanceira } from "@/models/consignado/propostaFinanceira.model";
import InsuranceDataCard from "../InsuranceDataCard";
import {
    editarPropostaFinanceira,
} from "@/services/consignado/conciliation.service";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import ModificationHistoryModal from "./modification-history-modal";
import { RelatorioConciliacao } from "@/models/consignado/relatorioConciliacao.model";

interface ConciliationDetailsItemsProps {
    proposal: PropostaFinanceira | undefined;
    relatorios: RelatorioConciliacao[] | null;
    handleConciliateClick: () => void;
    isLoading: boolean;
    dynamicSteps: any[];
}

const editableFields = [
    "shortHash",
    "valorLiberado",
    "valorParcela",
    "quantidadeParcela",
    "valorIof",
    "insuranceCompanyName",
    "valorSeguro",
    "valorDesconto",
    "dataCriacaoProposta",
    "dataPagamentoSeguro",
    "quantidadeParcelaSeguro",
    "valorLiberadoSeguro",
    "dataPagamentoBanco",
    "quantidadeParcelaBanco",
    "valorLiberadoBanco",
] as const;

const monetaryFields = [
    "valorLiberado",
    "valorParcela",
    "valorIof",
    "valorSeguro",
    "valorDesconto",
    "valorLiberadoSeguro",
    "valorLiberadoBanco",
] as const;

type EditableField = (typeof editableFields)[number];
type MonetaryField = (typeof monetaryFields)[number];

type EditableProposalValues = {
    [K in EditableField]: K extends
        | "shortHash"
        | "insuranceCompanyName"
        | "dataCriacaoProposta"
        | "dataPagamentoSeguro"
        | "dataPagamentoBanco"
        ? string
        : number;
};

const numberFields = new Set<EditableField>([
    "valorLiberado",
    "valorParcela",
    "quantidadeParcela",
    "valorIof",
    "valorSeguro",
    "valorDesconto",
    "quantidadeParcelaSeguro",
    "valorLiberadoSeguro",
    "quantidadeParcelaBanco",
    "valorLiberadoBanco",
]);

const monetaryFieldsSet = new Set<EditableField>(monetaryFields);

const defaultEditingFields: Record<EditableField, boolean> = Object.fromEntries(
    editableFields.map((field) => [field, false])
) as Record<EditableField, boolean>;

const getDefaultEditingFields = (): Record<EditableField, boolean> => ({
    ...defaultEditingFields,
});

const getEditableValuesFromProposal = (proposal: PropostaFinanceira): EditableProposalValues => ({
    shortHash: proposal.shortHash || "",
    valorLiberado: Number(proposal.valorLiberado ?? 0),
    valorParcela: Number(proposal.valorParcela ?? 0),
    quantidadeParcela: Number(proposal.quantidadeParcela ?? 0),
    valorIof: Number(proposal.valorIof ?? 0),
    insuranceCompanyName: proposal.insuranceCompanyName || "",
    valorSeguro: Number(proposal.valorSeguro ?? 0),
    valorDesconto: Number(proposal.valorDesconto ?? 0),
    dataCriacaoProposta: proposal.dataCriacaoProposta || "",
    dataPagamentoSeguro: proposal.dataPagamentoSeguro || "",
    quantidadeParcelaSeguro: Number(proposal.quantidadeParcelaSeguro ?? 0),
    valorLiberadoSeguro: Number(proposal.valorLiberadoSeguro ?? 0),
    dataPagamentoBanco: proposal.dataPagamentoBanco || "",
    quantidadeParcelaBanco: Number(proposal.quantidadeParcelaBanco ?? 0),
    valorLiberadoBanco: Number(proposal.valorLiberadoBanco ?? 0),
});

const getMonetaryInputs = (values: EditableProposalValues): Record<MonetaryField, string> => ({
    valorLiberado: BrlCurrencyFormatter(values.valorLiberado),
    valorParcela: BrlCurrencyFormatter(values.valorParcela),
    valorIof: BrlCurrencyFormatter(values.valorIof),
    valorSeguro: BrlCurrencyFormatter(values.valorSeguro),
    valorDesconto: BrlCurrencyFormatter(values.valorDesconto),
    valorLiberadoSeguro: BrlCurrencyFormatter(values.valorLiberadoSeguro),
    valorLiberadoBanco: BrlCurrencyFormatter(values.valorLiberadoBanco),
});

const isMonetaryField = (field: EditableField): field is MonetaryField => {
    return monetaryFieldsSet.has(field);
};

// const parseCurrencyInputToNumber = (value: string): number => {
//     const onlyDigits = value.replace(/\D/g, "");

//     if (!onlyDigits)
//         return 0;

//     return Number(onlyDigits) / 100;
// };

const parseCurrencyInputToNumber = (value: string): number => {
  const onlyDigits = value.replace(/\D/g, "");

  if (!onlyDigits) return 0;

  return parseInt(onlyDigits, 10) / 100;
};

const formatCurrencyDigitsToMask = (digits: string): string => {
  const sanitizedDigits = digits
    .replace(/\D/g, "")
    .replace(/^0+/, "");

  const padded = sanitizedDigits.padStart(3, "0");

  const integerPart = padded.slice(0, -2);
  const decimalPart = padded.slice(-2);

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return `${formattedInteger},${decimalPart}`;
};

// const formatCurrencyDigitsToMask = (digits: string): string => {
//     const sanitizedDigits = digits.replace(/\D/g, "");
//     const padded = sanitizedDigits.padStart(3, "0");
//     const integerPart = padded.slice(0, -2);
//     const decimalPart = padded.slice(-2);
//     const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

//     return `${formattedInteger},${decimalPart}`;
// };

const buildPatchPayload = (
    original: EditableProposalValues,
    edited: EditableProposalValues
): Partial<PropostaFinanceira> => {
    const payload: Partial<PropostaFinanceira> = {};

    editableFields.forEach((field) => {
        if (original[field] !== edited[field]) {
            const typedPayload = payload as Partial<Record<EditableField, string | number>>;
            typedPayload[field] = edited[field];
        }
    });

    return payload;
};

export default function ConciliationDetailsItems(props: ConciliationDetailsItemsProps) {
    const { proposal, relatorios, handleConciliateClick, isLoading, dynamicSteps } = props;

    const [editingFields, setEditingFields] = useState<Record<EditableField, boolean>>(getDefaultEditingFields);
    const [editedValues, setEditedValues] = useState<EditableProposalValues | null>(null);
    const [originalValues, setOriginalValues] = useState<EditableProposalValues | null>(null);
    const [monetaryInputs, setMonetaryInputs] = useState<Record<MonetaryField, string> | null>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isUsersModalOpen, setIsUsersModalOpen] = useState<boolean>(false);
    const toast = useToast();

    useEffect(() => {
        if (!proposal)
            return;

        const nextValues = getEditableValuesFromProposal(proposal);
        setEditedValues(nextValues);
        setOriginalValues(nextValues);
        setMonetaryInputs(getMonetaryInputs(nextValues));
        setEditingFields(getDefaultEditingFields());
    }, [proposal]);

    const isAnyFieldEditing = useMemo(() => {
        return Object.values(editingFields).some(Boolean);
    }, [editingFields]);

    const handleToggleAllFields = (): void => {
        if (!editedValues || !monetaryInputs) return;

        const allEditing = editableFields.every((field) => editingFields[field]);

        if (!allEditing) {
            setMonetaryInputs((previous) => {
            if (!previous) return previous;

            const updated = { ...previous };

            editableFields.forEach((field) => {
                if (isMonetaryField(field)) {
                updated[field] = BrlCurrencyFormatter(
                    Number(editedValues[field] ?? 0)
                );
                }
            });

            return updated;
            });
        }

        setEditingFields(
            Object.fromEntries(
            editableFields.map((field) => [field, !allEditing])
            ) as Record<EditableField, boolean>
        );
    };

    const handleChangeField = (field: EditableField, value: string): void => {
        if (!editedValues || !monetaryInputs)
            return;

        if (isMonetaryField(field)) {
            const onlyDigits = value.replace(/\D/g, "");
            const maskedValue = formatCurrencyDigitsToMask(onlyDigits);
            const numericValue = parseCurrencyInputToNumber(maskedValue);

            setMonetaryInputs((previous) => {
                if (!previous)
                    return previous;

                return {
                    ...previous,
                    [field]: maskedValue,
                };
            });

            setEditedValues((previous) => {
                if (!previous)
                    return previous;

                return {
                    ...previous,
                    [field]: numericValue,
                };
            });

            return;
        }

        setEditedValues((previous) => {
            if (!previous)
                return previous;

            return {
                ...previous,
                [field]: numberFields.has(field)
                    ? Number(value)
                    : value,
            };
        });
    };

    const handleCancelChanges = (): void => {
        if (!originalValues)
            return;

        setEditedValues(originalValues);
        setMonetaryInputs(getMonetaryInputs(originalValues));
        setEditingFields(getDefaultEditingFields());
    };

    const handleSaveChanges = async (): Promise<void> => {
        if (!proposal || !editedValues || !originalValues)
            return;

        const payload = buildPatchPayload(originalValues, editedValues);

        if (Object.keys(payload).length === 0) {
            toast({
                title: "Nenhuma alteração para salvar",
                status: "info",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsSaving(true);

        try {
            await editarPropostaFinanceira(proposal.id, payload);
            toast({
                title: "Proposta financeira atualizada",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            setOriginalValues(editedValues);
            setEditingFields(getDefaultEditingFields());
        } catch (error) {
            console.error("Erro ao atualizar proposta financeira:", error);
            toast({
                title: "Erro ao atualizar proposta",
                description: "Não foi possível salvar as alterações.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenModifierUsersModal = (): void => {
        setIsUsersModalOpen(true);
    };

    const handleCloseModifierUsersModal = (): void => {
        setIsUsersModalOpen(false);
    };

    if (!proposal || !proposal.id) {
        return <Text>Proposta não encontrada</Text>;
    }

    if (!editedValues || !monetaryInputs) {
        return <Text>Carregando dados da proposta...</Text>;
    }

    return (
        <VStack spacing={6} align="stretch" borderRadius="lg">
            <GranatechInternalDataCard
                editedValues={editedValues}
                editingFields={editingFields}
                monetaryInputs={monetaryInputs}
                handleToggleAllFields={handleToggleAllFields}
                onChangeField={handleChangeField}
                relatorios={relatorios}
                handleConciliateClick={handleConciliateClick}
                isLoading={isLoading}
                dynamicSteps={dynamicSteps}
            />
            <BMPDataCard
                proposal={proposal}
                editedValues={editedValues}
                editingFields={editingFields}
                monetaryInputs={monetaryInputs}
                onChangeField={handleChangeField}
            />
            <InsuranceDataCard
                proposal={proposal}
                editedValues={editedValues}
                editingFields={editingFields}
                monetaryInputs={monetaryInputs}
                onChangeField={handleChangeField}
            />

            {isAnyFieldEditing && (
                <Flex justifyContent="end" gap={2} mt={2}>
                    <Button variant="ghost" onClick={handleCancelChanges} isDisabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button colorScheme="primary" onClick={() => void handleSaveChanges()} isLoading={isSaving}>
                        OK
                    </Button>
                </Flex>
            )}

            <Flex justifyContent="end" mt={2}>
                <Button variant="outline" onClick={handleOpenModifierUsersModal}>
                    Usuários que modificaram o contrato
                </Button>
            </Flex>

            <ModificationHistoryModal
                isOpen={isUsersModalOpen}
                onClose={handleCloseModifierUsersModal}
                proposalId={proposal.id}
            />
        </VStack>
    );
}
